import Base from "@/api-services/base";
import Servicio from "@/api-services/nom035stps2018.service";

const BaseURL = `Nom035Publico`;
const Slot = "nom035Acceso";

/**
 * Acceso al cuestionario de la NOM-035 para quien llega con el enlace y sin sesión.
 *
 * Va aparte de nom035stps2018.service.js porque el contrato es el contrario: estos
 * endpoints son anónimos, se resuelven desde el linkID —el secreto que trae el
 * colaborador— y no desde la suscripción del navegador. Mezclarlos obligaría a que
 * cada método del servicio de siempre preguntara con cuál de los dos contratos va.
 *
 * **Nunca escribe localStorage.suscripcionID ni localStorage.sesion.** Base.ObtenURLSuscripcion
 * lee el primero para *todas* las llamadas de la aplicación, así que escribirlo aquí
 * repuntaría en silencio a otro inquilino la pantalla de un administrador de RH que
 * comparta el navegador. LogInOem.vue sí lo hace; no se copia.
 */
export default {
  /**
   * Prueba de identidad guardada, o null si no hay ninguna.
   * @returns {{linkID: string, evaluacionID: string, token: string, validadoHasta: string}|null}
   */
  getAcceso() {
    var Ls = localStorage[Slot];
    if (Ls == null || Ls == "") return null;
    try {
      return JSON.parse(Ls);
    } catch (e) {
      //Un valor ilegible se trata como si no hubiera: lo que sigue es volver a validar,
      //no dejar la pantalla en blanco.
      this.limpiaAcceso();
      return null;
    }
  },

  setAcceso(Acceso) {
    localStorage[Slot] = JSON.stringify(Acceso);
  },

  limpiaAcceso() {
    localStorage.removeItem(Slot);
  },

  /**
   * ¿Hay una prueba viva para esta evaluación?
   *
   * Es una comodidad del cliente para no pedir el código de más; **no es la puerta**.
   * La puerta está en el API, que vuelve a comprobar la huella del token en cada
   * llamada. Si alguien edita este espacio a mano, el servidor responde 403.
   *
   * @param {string} EvaluacionID evaluación que se quiere abrir
   */
  EsAccesoValido(EvaluacionID) {
    var A = this.getAcceso();
    if (A == null || !A.token) return false;
    if (EvaluacionID != null && A.evaluacionID != EvaluacionID) return false;
    if (!A.validadoHasta) return false;
    return new Date(A.validadoHasta) > new Date();
  },

  /**
   * Agrega el encabezado por el que viaja la prueba, respetando las opciones que llegan.
   */
  ObtenOpciones(Opciones) {
    var A = this.getAcceso();
    if (A == null || !A.token) return Opciones;
    if (Opciones == null) Opciones = {};
    if (Opciones.headers == null) Opciones.headers = {};
    Opciones.headers.ValidacionNom035 = A.token;
    return Opciones;
  },

  // ---------------------------------------------------------------- verificación

  /**
   * Pregunta qué hace falta para contestar el cuestionario de este enlace.
   *
   * No manda nada: es la consulta que dice si ya está validado, si hay que elegir
   * medio, o si el colaborador no tiene ni correo ni teléfono en su ficha.
   */
  GetVerificacion(Vista, linkID, OnCorrecto, OnErroneo, OnFinalizado) {
    var URL = `${Base.URL}/${BaseURL}/${linkID}/Verificacion`;
    return Base.get(
      Vista,
      URL,
      this.ObtenOpciones(null),
      OnCorrecto,
      OnErroneo,
      OnFinalizado
    );
  },

  /**
   * Pide que manden el código.
   * @param {*} Datos { Metodo, DestinoID } o { Metodo, DestinoCapturado }
   */
  PostVerificacion(Vista, linkID, Datos, OnCorrecto, OnErroneo, OnFinalizado) {
    var URL = `${Base.URL}/${BaseURL}/${linkID}/Verificacion`;
    return Base.post(
      Vista,
      URL,
      Datos,
      this.ObtenOpciones(null),
      OnCorrecto,
      OnErroneo,
      OnFinalizado
    );
  },

  /**
   * Contesta el código. Si es el correcto, guarda el token que entrega el servidor.
   *
   * El token se emite una sola vez —del lado del servidor sólo queda su huella—, así
   * que se guarda aquí mismo y no en la vista, para que no se pierda al navegar.
   */
  PutVerificacion(
    Vista,
    linkID,
    EvaluacionID,
    Datos,
    OnCorrecto,
    OnErroneo,
    OnFinalizado
  ) {
    var URL = `${Base.URL}/${BaseURL}/${linkID}/Verificacion`;
    var Yo = this;
    return Base.put(
      Vista,
      URL,
      Datos,
      this.ObtenOpciones(null),
      (response) => {
        var R = response.data;
        if (R && R.token) {
          //Cuánto dura la prueba lo decide el servidor y no viaja como número, así que
          //se deduce del vencimiento que acaba de entregar. Sirve para correr la
          //vigencia en cada guardado sin inventarse una duración.
          var Minutos = Math.round(
            (new Date(R.validadoHasta) - new Date()) / 60000
          );
          Yo.setAcceso({
            linkID: linkID,
            evaluacionID: EvaluacionID,
            token: R.token,
            validadoHasta: R.validadoHasta,
            minutos: Minutos > 0 ? Minutos : 5,
          });
        }
        if (OnCorrecto != undefined) OnCorrecto(response);
      },
      OnErroneo,
      OnFinalizado
    );
  },

  // ------------------------------------------------------------------ evaluación

  /**
   * Lee la evaluación por donde corresponda.
   *
   * Con prueba viva va por el camino público, con el linkID; sin ella delega en el
   * servicio de siempre. Esa delegación es lo que deja intactas las tres entradas de
   * RH y la tarjeta del Autoservicio: para ellas esta función es la de antes.
   */
  GetEvaluacion(Vista, EvaluacionID, OnCorrecto, OnErroneo, OnFinalizado) {
    var A = this.getAcceso();
    if (!this.EsAccesoValido(EvaluacionID))
      return Servicio.GetNom035Stps2018Evaluacion(
        Vista,
        EvaluacionID,
        OnCorrecto,
        OnErroneo,
        OnFinalizado
      );

    var URL = `${Base.URL}/${BaseURL}/${A.linkID}/Evaluacion`;
    return Base.get(
      Vista,
      URL,
      this.ObtenOpciones(null),
      OnCorrecto,
      OnErroneo,
      OnFinalizado
    );
  },

  /**
   * Guarda las respuestas por donde corresponda. Ver GetEvaluacion.
   */
  PutEvaluacion(Vista, EvaluacionID, Datos, OnCorrecto, OnErroneo, OnFinalizado) {
    var A = this.getAcceso();
    if (!this.EsAccesoValido(EvaluacionID))
      return Servicio.PutNom035Stps2018Evaluacion(
        Vista,
        EvaluacionID,
        Datos,
        OnCorrecto,
        OnErroneo,
        OnFinalizado
      );

    var URL = `${Base.URL}/${BaseURL}/${A.linkID}/Evaluacion`;
    var Yo = this;
    return Base.put(
      Vista,
      URL,
      Datos,
      this.ObtenOpciones(null),
      (response) => {
        //El servidor corre la vigencia en cada guardado; se refleja aquí para que el
        //cliente no crea que caducó antes de tiempo y pida el código de más.
        var Acceso = Yo.getAcceso();
        if (Acceso != null) {
          var Minutos = Acceso.minutos > 0 ? Acceso.minutos : 5;
          Acceso.validadoHasta = new Date(
            new Date().getTime() + Minutos * 60000
          ).toISOString();
          Yo.setAcceso(Acceso);
        }
        if (OnCorrecto != undefined) OnCorrecto(response);
      },
      OnErroneo,
      OnFinalizado
    );
  },
};
